import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updatePrescribersProfileList,
  updatePrescribersProfileIndexOnList,
  resetError,
} from "./PrescribersProfileList.actions";
import withRouter from "utils/withRouter";
import ResetListState from "components/ResetListState";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { PrescribersProfileConstants } from "lookup/PrescribersProfileConstants";

import { showNotification } from "services/NotificationService";
import ServerSideReactTable from 'commons/table/ServerSideReactTable';
import { FilterTypes, ColumnTypes } from 'commons/table/Constants';
import { WrapTextAndCenterAlign } from "components/WrapText";
import { Badge } from "react-bootstrap";
import UpdatePrescriberInfo from "components/UpdatePrescriberInfo";

const Tip = (props) => (
  <div style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const DEFAULT_PAGE_SIZE = 15;

const statesFilterMap = [];
PrescribersProfileConstants.PRESCRIBER_STATES.forEach((state, i) =>
  statesFilterMap.push({ id: state.key, name: state.value })
);

const PrescribersProfileList = () => {
  const dispatch = useDispatch();

  const prescribersList = useSelector((state) => state.prescribersProfileList);

  const initialListState = useMemo(() => {
    let listState = prescribersList.listState;
    if (!listState) {
      listState = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_SORTING,
        filtered: PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_FILTER,
        resized: [],
        expanded: {},
      };
    }
    return listState;
  }, [prescribersList.listState]);

  const [data, setData] = useState(prescribersList.data || []);
  const [pages, setPages] = useState(prescribersList.pages || 0);
  const [totalElements, setTotalElements] = useState(
    prescribersList.totalElements !== undefined
      ? prescribersList.totalElements
      : "Loading ..."
  );
  const [loading, setLoading] = useState(prescribersList.loading);
  const [listState, setListState] = useState(initialListState);

  const updateData = useCallback(
    (state, preventUpdate) => {
      if (!preventUpdate) {
        const nextState = { ...state };
        nextState.filtered = nextState.filtered.filter((element) => {
          if (
            typeof element.value === "string" ||
            Array.isArray(element.value)
          ) {
            return element.value.length > 0;
          }
          if (typeof element.value === "boolean") {
            return true; // Keep both true and false boolean values
          }
          return element.value !== undefined && element.value !== null;
        });
        if (
          nextState.sorted !==
          PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_SORTING
        ) {
          nextState.sorted = nextState.sorted.map((element) => ({
            ...element,
            isDefault: false,
          }));
        }

        dispatch(
          updatePrescribersProfileList(
          nextState.pageSize,
          nextState.page,
          nextState.sorted,
          nextState.filtered,
          nextState.resized,
          nextState.expanded
          )
        );
      } else {
        setListState(state);
      }
    },
    [dispatch]
  );

  const onUpdateTable = useCallback(
    (filterValues, sortBy, pageSize, pageNo) => {
      const nextState = {
        ...listState,
        pageSize,
        page: pageNo,
        sorted: sortBy ? sortBy : [],
        filtered: filterValues,
      };
      setListState(nextState);
      updateData(nextState);
    },
    [listState, updateData]
  );

  useEffect(() => {
    updateData(listState);
  }, []);

  useEffect(() => {
    setData(prescribersList.data || []);
    setPages(prescribersList.pages || 0);
    setTotalElements(
      prescribersList.totalElements !== undefined
        ? prescribersList.totalElements
        : "Loading ..."
    );
    setLoading(prescribersList.loading);
    setListState(initialListState);
  }, [prescribersList, initialListState]);

  useEffect(() => {
    if (prescribersList.isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      dispatch(resetError());
    }
  }, [prescribersList.isError, dispatch]);

  const renderActions = useCallback(
    (rowData) => (
      <span className="action-button">
        <UpdatePrescriberInfo
          prescriber={rowData}
          isDisabled={false}
          updateData={() => updateData(listState, false)}
          isFromAW={false}
          activityType={null}
          isFromPrescriberProfileList={true}
        />
      </span>
    ),
    [listState, updateData]
  );

  const columns = useMemo(
    () => [
      {
        header: "Prescriber Name",
        accessor: "prescriberName",
        accessorKey: "prescriberName",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.SearchableInput,
      },
      {
        header: "Prescriber NPI",
        accessor: "prescriberNpi",
        accessorKey: "prescriberNpi",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.SearchableInput,
      },
      {
        header: "Prescriber Phone",
        accessor: "prescriberPhone",
        accessorKey: "prescriberPhone",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.SearchableInput,
      },
      {
        header: "Prescriber Fax",
        accessor: "prescriberFax",
        accessorKey: "prescriberFax",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.SearchableInput,
      },
      {
        header: "Prescriber Address",
        accessor: "prescriberCityState",
        accessorKey: "prescriberCityState",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.SearchableInput,
      },
      {
        header: "Practice Name",
        accessor: "prescriberPractice",
        accessorKey: "prescriberPractice",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.SearchableInput,
      },
      {
        header: "Status",
        accessor: "state",
        accessorKey: "state",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Text,
        filterType: FilterTypes.MultiSelect,
        filterOptions: statesFilterMap,
      },
      {
        header: "Opted Out",
        accessor: "optedOut",
        accessorKey: "optedOut",
        width: 100,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Custom,
        filterType: FilterTypes.SingleSelect,
        filterOptions: PrescribersProfileConstants.OPTED_OUT_FILTER,
        filterReturnKey: true,
        customComponent: (value) => (
          <WrapTextAndCenterAlign>
            {value ? (
              <Badge bg="secondary">True</Badge>
            ) : (
              <Badge bg="default-gray">False</Badge>
            )}
          </WrapTextAndCenterAlign>
        ),
      },
      {
        header: "Action",
        accessor: "action",
        accessorKey: "action",
        enableSorting: false,
        enableFiltering: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) => renderActions(row.original),
      },
    ],
    [renderActions]
  );

  return (
    <Container className="list-container" fluid>
      <Row>
        <Col md={12}>
          <div className="codex-version-display-bar">
            <span style={{ float: "left" }}>
              {" "}
              Total Prescribers: <strong>{totalElements}</strong>
            </span>
            <strong style={{ float: "center" }}>Prescribers List</strong>
            <ResetListState
              onReset={() =>
                updateData(
                  {
                    ...listState,
                    filtered:
                      PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_FILTER,
                    sorted:
                      PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_SORTING,
                  },
                  false
                )
              }
              resetterDisabled={
                listState.filtered ===
                  PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_FILTER &&
                listState.sorted ===
                  PrescribersProfileConstants.DEFAULT_PRESCRIBER_LIST_SORTING
              }
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            totalPages={pages}
            totalRecords={totalElements == "loading" ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            addRowSelection={false}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            onUpdateTableData={onUpdateTable}
          />
          <Tip>
            Tip: Hold shift when sorting to multi-sort and disable sort!
          </Tip>
        </Col>
      </Row>
    </Container>
  );
};

export default withRouter(PrescribersProfileList);
